/**
 * Returns the attachment's file-type ID regardless of which shape the object is in
 * (FileManager emits `fileType.id`; formatAttachments also sets `file_type_id`).
 *
 * @param {Object} attachment
 * @returns {string|number}
 */
const getAttachmentTypeId = attachment =>
  attachment?.fileType?.id ?? attachment?.file_type_id ?? '';

/**
 * Builds a stable string key for an attachment used for equality checks.
 * Intentionally ignores transient fields (isLoading, message, type) that
 * differ between the baseline snapshot shape and the FileManager runtime shape.
 *
 * @param {Object} attachment
 * @returns {string}
 */
const buildAttachmentComparableKey = attachment =>
  `${attachment?.id ?? ''}:${getAttachmentTypeId(attachment)}`;

/**
 * Returns true when both flat file arrays represent the same set of attachments
 * (same ids and file-types), regardless of object shape or field-order differences.
 *
 * @param {Object[]} leftAttachments
 * @param {Object[]} rightAttachments
 * @returns {boolean}
 */
export const areAttachmentsEquivalent = (leftAttachments = [], rightAttachments = []) => {
  if (leftAttachments.length !== rightAttachments.length) return false;

  const leftKeys = leftAttachments.map(buildAttachmentComparableKey).sort();
  const rightKeys = rightAttachments.map(buildAttachmentComparableKey).sort();

  return leftKeys.every((key, index) => key === rightKeys[index]);
};
